import type { AssetIconResolver, AssetImageResolver } from '../components/base/BaseAssetIcon';
import { useContext, useMemo, type ReactNode } from 'react';

import type { BaseAppearance } from '../components/base/types';
import type { DataExplorerAppearance } from '../components/data-explorer/types';
import {
  resolveMonkeysLocale,
  type MonkeysLocaleInput,
  type MonkeysLocaleMessagesOverride,
} from '../locale';
import {
  compileThemeTokens,
  type CompiledThemeTokens,
  type MonkeysThemeColorMode,
  type MonkeysThemeDensity,
  type ThemeTokens,
} from '../theme-system';
import {
  MonkeysDirectionContext,
  MonkeysEnvironmentContext,
  MonkeysLocaleContext,
  MonkeysThemeContext,
  type MonkeysDirection,
  type MonkeysEnvironment,
  type MonkeysPortalOptions,
  type MonkeysStatusStatesConfig,
  type MonkeysListFooterConfig,
} from './context';

export interface MonkeysProviderAppearance {
  base?: BaseAppearance;
  dataExplorer?: DataExplorerAppearance;
}

export interface MonkeysProviderProps {
  children?: ReactNode;
  locale?: MonkeysLocaleInput;
  messages?: MonkeysLocaleMessagesOverride;
  direction?: MonkeysDirection;
  theme?: ThemeTokens;
  themeId?: string;
  colorMode?: MonkeysThemeColorMode;
  density?: MonkeysThemeDensity;
  appearance?: MonkeysProviderAppearance;
  environment?: MonkeysEnvironment;
  iconResolver?: AssetIconResolver;
  imageResolver?: AssetImageResolver;
  portal?: MonkeysPortalOptions;
  statusStates?: MonkeysStatusStatesConfig;
  listFooter?: MonkeysListFooterConfig;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validateDirection(direction: MonkeysDirection | undefined) {
  if (direction !== undefined && direction !== 'ltr' && direction !== 'rtl') {
    throw new TypeError('MonkeysProvider direction must be "ltr" or "rtl".');
  }
}

function validateEnvironment(environment: MonkeysEnvironment | undefined) {
  if (environment === undefined) return;
  if (!isRecord(environment)) {
    throw new TypeError('MonkeysProvider environment must be an object.');
  }
  if (
    environment.getDocument !== undefined &&
    typeof environment.getDocument !== 'function'
  ) {
    throw new TypeError(
      'MonkeysProvider environment.getDocument must be a function.',
    );
  }
  if (
    environment.getWindow !== undefined &&
    typeof environment.getWindow !== 'function'
  ) {
    throw new TypeError(
      'MonkeysProvider environment.getWindow must be a function.',
    );
  }
}

function validatePortal(portal: MonkeysPortalOptions | undefined) {
  if (portal === undefined) return;
  if (!isRecord(portal)) {
    throw new TypeError('MonkeysProvider portal must be an object.');
  }
  if (portal.disabled !== undefined && typeof portal.disabled !== 'boolean') {
    throw new TypeError('MonkeysProvider portal.disabled must be a boolean.');
  }
  if (portal.container == null) return;
  if (
    typeof portal.container !== 'function' &&
    typeof portal.container !== 'object'
  ) {
    throw new TypeError(
      'MonkeysProvider portal.container must be an Element, null, or a function.',
    );
  }
}

function resolveProviderTheme(
  theme: ThemeTokens | undefined,
  colorMode: MonkeysThemeColorMode | undefined,
  density: MonkeysThemeDensity | undefined,
  inherited: CompiledThemeTokens | undefined,
) {
  if (!theme) return inherited;
  return compileThemeTokens(theme, { colorMode, density });
}

export function MonkeysProvider({
  children,
  locale,
  messages,
  direction,
  theme,
  themeId,
  colorMode,
  density,
  appearance,
  environment,
  iconResolver,
  imageResolver,
  portal,
  statusStates,
  listFooter,
}: MonkeysProviderProps) {
  const parentLocale = useContext(MonkeysLocaleContext);
  const parentTheme = useContext(MonkeysThemeContext);
  const parentDirection = useContext(MonkeysDirectionContext);
  const parentEnvironment = useContext(MonkeysEnvironmentContext);

  validateDirection(direction);
  validateEnvironment(environment);
  if (iconResolver !== undefined && typeof iconResolver !== 'function') {
    throw new TypeError('MonkeysProvider iconResolver must be a function.');
  }
  if (imageResolver !== undefined && typeof imageResolver !== 'function') {
    throw new TypeError('MonkeysProvider imageResolver must be a function.');
  }
  validatePortal(portal);

  const resolvedLocale = useMemo(
    () => resolveMonkeysLocale(locale, messages, parentLocale),
    [locale, messages, parentLocale],
  );
  const localeValue = useMemo(
    () => ({ ...resolvedLocale, provided: true }),
    [resolvedLocale],
  );
  const resolvedTheme = useMemo(
    () =>
      resolveProviderTheme(
        theme,
        colorMode,
        density,
        parentTheme.resolvedTheme,
      ),
    [colorMode, density, parentTheme.resolvedTheme, theme],
  );
  const themeValue = useMemo(
    () => ({
      provided: true,
      resolvedTheme,
      themeId: themeId ?? parentTheme.themeId,
      baseAppearance: {
        ...parentTheme.baseAppearance,
        ...appearance?.base,
        ...(density ? { density } : undefined),
        slots: {
          ...parentTheme.baseAppearance?.slots,
          ...appearance?.base?.slots,
        },
        styles: {
          ...parentTheme.baseAppearance?.styles,
          ...appearance?.base?.styles,
        },
      },
      dataExplorerAppearance: {
        ...parentTheme.dataExplorerAppearance,
        ...appearance?.dataExplorer,
        ...(density ? { density } : undefined),
        slots: {
          ...parentTheme.dataExplorerAppearance?.slots,
          ...appearance?.dataExplorer?.slots,
        },
        styles: {
          ...parentTheme.dataExplorerAppearance?.styles,
          ...appearance?.dataExplorer?.styles,
        },
      },
      statusStates: {
        ...parentTheme.statusStates,
        ...statusStates,
        loading: { ...parentTheme.statusStates?.loading, ...statusStates?.loading },
        empty: { ...parentTheme.statusStates?.empty, ...statusStates?.empty },
        error: { ...parentTheme.statusStates?.error, ...statusStates?.error },
        permission: { ...parentTheme.statusStates?.permission, ...statusStates?.permission },
      },
      listFooter: {
        ...parentTheme.listFooter,
        ...listFooter,
        ...(listFooter?.pageSizeOptions ? { pageSizeOptions: [...listFooter.pageSizeOptions] } : undefined),
      },
    }),
    [
      appearance?.base,
      appearance?.dataExplorer,
      density,
      parentTheme.baseAppearance,
      parentTheme.dataExplorerAppearance,
      parentTheme.statusStates,
      parentTheme.themeId,
      resolvedTheme,
      statusStates,
      listFooter,
      themeId,
    ],
  );
  const directionValue = useMemo(
    () => ({
      provided: true,
      direction: direction ?? parentDirection.direction ?? 'ltr',
    }),
    [direction, parentDirection.direction],
  );
  const environmentValue = useMemo(
    () => ({
      provided: true,
      iconResolver: iconResolver ?? parentEnvironment.iconResolver,
      imageResolver: imageResolver ?? parentEnvironment.imageResolver,
      environment: {
        ...parentEnvironment.environment,
        ...environment,
      },
      portal: {
        ...parentEnvironment.portal,
        ...portal,
      },
    }),
    [
      environment,
      parentEnvironment.environment,
      parentEnvironment.iconResolver,
      iconResolver,
      imageResolver,
      parentEnvironment.imageResolver,
      parentEnvironment.portal,
      portal,
    ],
  );

  return (
    <MonkeysLocaleContext.Provider value={localeValue}>
      <MonkeysThemeContext.Provider value={themeValue}>
        <MonkeysDirectionContext.Provider value={directionValue}>
          <MonkeysEnvironmentContext.Provider value={environmentValue}>
            {children}
          </MonkeysEnvironmentContext.Provider>
        </MonkeysDirectionContext.Provider>
      </MonkeysThemeContext.Provider>
    </MonkeysLocaleContext.Provider>
  );
}
